import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';

import Analytics from 'analytics-opencare';
import config from 'app.config';
import { ValidationRulePreset } from 'common/Validator/ValidationRules';
import { createImmutableFormValidatorConfig, FieldValidation, useFormValidator } from 'common/Validator/Validator';
import { loginUser } from 'connect/actions';
import { selectDataLoginFormError, selectDataUserAuthenticated } from 'connect/selectors';
import { selectDataLoadingLogin } from 'connect/selectors';
import { Input, InputType } from 'pages/checkout/components/common/Input';
import { CheckoutLink, CheckoutLinkButton, FormError } from 'pages/checkout/components/common/input-styled-components';
import { Form } from 'pages/checkout/components/common/layout-styled-components';
import { useNextCheckoutStep } from 'pages/checkout/hooks/checkoutStepHooks';
import { useCheckoutRedesignAnalyticsData } from 'pages/checkout/utils/analyticsUtils';

import { AlternateLoginSection, FormFieldsWrapper, SubmitButton } from '../Info-styled-components';
import { DividerWithText, LoginText } from '../Info-styled-components';

import { SsoButtons } from './SsoButtons/SsoButtons';

const analytics = new Analytics();

interface LoginFormProps {
  setSignUp: () => void;
}

interface ValidationState {
  allValid?: boolean,
  email?: FieldValidation,
  password?: FieldValidation,
}

const LOGIN_FORM_VALIDATOR_CONFIG = createImmutableFormValidatorConfig<void>([
  { field: 'email', preset: ValidationRulePreset.email },
  { field: 'password', preset: ValidationRulePreset.required },
]);

const invalidCredentialsError = 'The email or password you entered is incorrect.';

const getPrettyLoginError = (loginError: string) => {
  if (!loginError) {
    return null;
  }

  const errorToMessageMap = [
    {
      errorSubstring: 'unknown user',
      errorMessage: invalidCredentialsError,
    },
    {
      errorSubstring: 'invalid credentials',
      errorMessage: invalidCredentialsError,
    },
    {
      errorSubstring: 'password is not set',
      errorMessage: 'A password has not been set for this account. Please reset it or contact support.',
    },
    {
      errorMessage: 'this email address is invalid',
      errorSubstring: 'The email address is not valid.',
    },
  ];

  const prettyLoginError = errorToMessageMap.find(
    error => loginError.toLowerCase().includes(error.errorSubstring.toLowerCase()),
  )?.errorMessage;

  return prettyLoginError ?? 'An unknown error occurred';
};

export const LoginForm = ({ setSignUp }: LoginFormProps) => {
  const { goToNextStep } = useNextCheckoutStep();
  const history = useHistory();
  const analyticsData = useCheckoutRedesignAnalyticsData();
  const dispatch = useDispatch();
  const formValidator = useFormValidator(LOGIN_FORM_VALIDATOR_CONFIG);
  // TODO we should move all redux calls to the SagaHandler system
  const isUserLoggedIn = useSelector(selectDataUserAuthenticated);
  const loadingLogin = useSelector(selectDataLoadingLogin);
  const loginError = useSelector(selectDataLoginFormError);
  const prettyLoginError = useMemo(() =>
    getPrettyLoginError(loginError) , [loginError]);

  const [email, setEmail] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [formValidity, setFormValidity] = useState<ValidationState>({});
  const [submitted, setSubmitted] = useState(false);
  const [showValidationErrors, setShowValidationErrors] = useState(false);

  const validationState = useMemo(() => ({
    email,
    password,
  }), [email, password]);

  const handleValidate = useCallback(() => {
    const validationResult = formValidator.validate(validationState);
    setFormValidity(validationResult);
    return validationResult;
  }, [validationState, formValidator]);

  // Validate when inputs change
  useEffect(() => {
    if (!validationState) {
      return;
    }

    handleValidate();
  }, [handleValidate, validationState]);

  const handleSubmit = (e: React.FormEvent<HTMLFormElement>) => {
    // Don't refresh
    e.preventDefault();

    const validationResult = handleValidate();

    if (!validationResult.allValid) {
      setShowValidationErrors(true);
      return;
    }

    setSubmitted(true);

    analytics.trackForm({
      pageType: analytics.pageTypes.FORM,
      pageName: 'checkout-info',
      name: 'checkout',
      section: 'login',
      content: {
        email_provided: String(!!email),
        ...analyticsData.content,
      },
    });

    dispatch(loginUser(email, password));
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newEmail = e.target.value;
    setEmail(newEmail);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newPassword = e.target.value;
    setPassword(newPassword);
  };

  useEffect(() => {
    if (submitted && !loadingLogin && !loginError && isUserLoggedIn) {
      goToNextStep();
    }
  }, [
    submitted,
    loadingLogin,
    goToNextStep,
    history,
    loginError,
    isUserLoggedIn,
  ]);

  const shouldShowError = prettyLoginError && !loadingLogin;

  // If form is invalid, show validation errors when clicking the button
  const handleDisabledButtonClick = () => {
    if (formValidity.allValid) {
      return;
    }

    setShowValidationErrors(true);
  };

  const handleSignUpClick = () => {
    analytics.trackClick({
      pageType: analytics.pageTypes.FORM,
      pageName: 'checkout-info',
      name: 'checkout',
      section: 'login',
      content: {
        action: 'sign-up-click',
        ...analyticsData.content,
      },
    });

    setSignUp();
  };

  return (
    <>
      {shouldShowError && <FormError>{prettyLoginError}</FormError>}

      <Form onSubmit={handleSubmit}>
        <FormFieldsWrapper>
          <Input
            type={InputType.EMAIL}
            placeholder="Enter your email"
            autoComplete="email"
            name="email"
            value={email}
            onChange={handleEmailChange}
            errorMessage={showValidationErrors ? formValidity.email?.message : undefined}
          />
          <Input
            type={InputType.PASSWORD}
            placeholder="Enter your password"
            autoComplete="password"
            name="password"
            value={password}
            onChange={handlePasswordChange}
            errorMessage={showValidationErrors ? formValidity.password?.message : undefined}
          />
        </FormFieldsWrapper>

        <CheckoutLink
          href={`${config.PATIENT_APP_URL}/#/forgot-password/`}
          target="_blank"
          rel="noopener noreferrer"
        >
          Forgot your password?
        </CheckoutLink>

        <SubmitButton
          $loading={loadingLogin}
          $disabled={!formValidity.allValid}
          onClick={handleDisabledButtonClick}
        >
          Log in
        </SubmitButton>
      </Form>
      <AlternateLoginSection>
        <DividerWithText>or</DividerWithText>
        <SsoButtons />
        <LoginText>
          First time booking?{' '}
          <CheckoutLinkButton onClick={handleSignUpClick} disabled={loadingLogin}>Sign up</CheckoutLinkButton>
        </LoginText>
      </AlternateLoginSection>
    </>
  );
};